import { ROUTES } from "@/shared/routing/routes"
import { SyncBackBody, SyncBackPet } from "@/shared/types/lead-api-types"

export type RouteKey = "prepackages" | "medicalHistory" | "ownerMailPhone" | "petBirthdate"

export type RouteResolverContext = {
  quote: Partial<SyncBackBody>
  termsAccepted: boolean
  isFromLandingPage?: boolean
  currentPetIdx?: number | null
  isEditingSinglePet?: boolean
}

export type RouteResolverResult = {
  route: string
  nextPetIndex?: number | null
}

const hasSelectedPackage = (pet: Partial<SyncBackPet>) =>
  Boolean(pet.pricing?.options?.pack)

export const isMultiPet = (quote?: Partial<SyncBackBody>) =>
  (quote?.pets?.length || 0) > 1

export const getNextPetNeedingPackage = (
  pets?: Partial<SyncBackPet>[]
): number | null => {
  if (!pets || pets.length === 0) {
    return null
  }

  const sortedPets = [...pets]
    .filter((pet): pet is Partial<SyncBackPet> & { idx: number } => typeof pet.idx === "number")
    .sort((a, b) => a.idx - b.idx)

  const targetPet = sortedPets.find((pet) => !hasSelectedPackage(pet))

  return targetPet?.idx ?? null
}

export const shouldReturnToTribeQuoteAfterBirthdate = (context: RouteResolverContext) =>
  Boolean(context.termsAccepted) ||
  // Conversion phase: at least one existing pet already has a formula,
  // so adding a new pet should return to the tribe-quote hub, not /tribe.
  // Email is avoided as a signal because the backend stores "_" as
  // placeholder when a landing page creates the quote without an email.
  Boolean(context.quote?.pets?.some(hasSelectedPackage))

export const getNextRoute = (
  currentRoute: RouteKey,
  context: RouteResolverContext
): RouteResolverResult => {
  switch (currentRoute) {
    case "prepackages": {
      // Editing a single pet's formula from /tribeQuote → go straight back to
      // the hub instead of continuing the per-pet flow.
      if (context.isEditingSinglePet) {
        return { route: ROUTES.tribeQuote.path }
      }
      // Interleaved per-pet flow: after choosing this pet's package, ask its
      // health history (same pet) before moving on to the next pet.
      return { route: ROUTES.medicalHistory.path }
    }
    case "medicalHistory": {
      // Editing a single pet from /tribeQuote bypasses the walk and returns to
      // the hub directly.
      if (context.isEditingSinglePet) {
        return { route: ROUTES.tribeQuote.path }
      }
      // Interleaved per-pet flow: move on to the next pet's prepackages, or
      // finish at /tribeQuote once every pet is done.
      const currentIdx = context.currentPetIdx
      if (typeof currentIdx === "number") {
        const nextPet = [...(context.quote?.pets || [])]
          .filter(
            (pet): pet is Partial<SyncBackPet> & { idx: number } =>
              typeof pet.idx === "number" && pet.idx > currentIdx
          )
          .sort((a, b) => a.idx - b.idx)[0]
        if (nextPet) {
          return { route: ROUTES.prepackages.path, nextPetIndex: nextPet.idx }
        }
      }
      return { route: ROUTES.tribeQuote.path }
    }
    case "ownerMailPhone":
      return {
        route: ROUTES.prepackages.path,
        nextPetIndex: Math.min(...(context.quote?.pets?.map((pet) => pet.idx) || [])),
      }
    case "petBirthdate": {
      if (shouldReturnToTribeQuoteAfterBirthdate(context)) {
        const nextPetIndex = getNextPetNeedingPackage(context.quote?.pets)
        if (nextPetIndex !== null) {
          return { route: ROUTES.prepackages.path, nextPetIndex }
        }
        return { route: ROUTES.tribeQuote.path }
      }
      return { route: ROUTES.tribe.path }
    }
  }
}
